import React, { useRef, useState } from 'react';

const style = {
  position: 'absolute',
  inset: 0,
  background: 'transparent',
  cursor: 'pointer',
} as const;

export type ImagePickerDropzoneProps = {
  qaId?: string;
  onDragEnter: () => void;
  onDragLeave: () => void;
  onPointerOver: () => void;
  onPointerLeave: () => void;
  onError: (error: Error) => void;
  onDrop: (file: File) => void;
  accept?: string[];
  maxSize?: number;
  id?: string;
};

export function ImagePickerDropzoneInternal({
  onDragEnter,
  onDragLeave,
  onError,
  onDrop,
  accept,
  maxSize,
  qaId,
  onPointerOver,
  onPointerLeave,
  id,
  getDroppedFile,
  triggerClick,
}: ImagePickerDropzoneProps & {
  getDroppedFile: (e: React.DragEvent) => Promise<File>;
  triggerClick: (e: React.UIEvent, fileInput: HTMLInputElement) => void;
}) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (fileToValidate: File): string | undefined => {
    if (accept && !accept.includes(fileToValidate.type))
      return 'File type not accepted';
    if (maxSize && fileToValidate.size > maxSize) return 'File is too large';
    return undefined;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (isDraggingOver) return;

    setIsDraggingOver(true);
    onDragEnter();
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
    onDragLeave();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();

    const droppedFile = await getDroppedFile(e);
    const validationError = validateFile(droppedFile);

    if (validationError) {
      setIsDraggingOver(false);
      onError(new Error(validationError));
      return;
    }

    setIsDraggingOver(false);
    onDrop(droppedFile);
  };

  const handleClick = (e: React.UIEvent) => {
    if (!fileInputRef.current) return;
    triggerClick(e, fileInputRef.current);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validationError = validateFile(selectedFile);
    if (validationError) return onError(new Error(validationError));

    onDrop(selectedFile);
  };

  return (
    <>
      <div
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
        role="button"
        tabIndex={0}
        aria-label="Upload file"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-misused-promises
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick(e)}
        onPointerOver={onPointerOver}
        onPointerLeave={onPointerLeave}
        style={style}
        data-qa-id={qaId}
        id={id}
      />
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        accept={accept?.join(',')}
        style={{ display: 'none' }}
      />
    </>
  );
}
